/**
 * C1 — Calculadora de góndolas por metros lineales (docs/calculadoras.md).
 *
 * Arriba: lógica pura sin DOM (la prueban tests/calc-gondolas.test.mjs).
 * Abajo: el web component <calc-gondolas>, que lee los datos que imprime
 * sections/calc-gondolas.liquid en <script type="application/json" data-calc-data>.
 *
 * Precios en centavos (como variant.price de Shopify). Medidas en metros.
 */

const EPS = 1e-6;
const TIPOS = ['central', 'pared'];
// «modulo»: módulo completo que se arma solo (sin inicial/continuación): producto con tipo_gondola y sin rol_kit.
const ROLES = ['inicial', 'continuacion', 'cabecera', 'estante_extra', 'modulo'];
const MAX_LARGO = 100;
const MAX_LINEAS = 50;

const toNumber = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const sameHeight = (a, b) => a != null && b != null && Math.abs(a - b) < 0.005;
const round2 = (n) => Math.round(n * 100) / 100;

/** Normaliza una variante tal como la serializa la section. */
export function normalizeVariant(raw) {
  const tipo = TIPOS.includes(raw.tipo) ? raw.tipo : null;
  const rol = ROLES.includes(raw.rol) ? raw.rol : tipo && !raw.rol ? 'modulo' : null;
  return {
    id: raw.id,
    handle: raw.handle || '',
    title: raw.title || '',
    // Estado de compra (snippets/product-state.liquid): «comprable» (o sin dato) y «preventa» van al carrito (la preventa con su
    // propiedad de línea «Entrega», ADR 0010); «proximamente» se cotiza y avisa.
    state: raw.state || 'comprable',
    // Propiedad de línea de la preventa ({ key, value }) o null.
    delivery: raw.delivery && raw.delivery.key && raw.delivery.value ? { key: String(raw.delivery.key), value: String(raw.delivery.value) } : null,
    // Color (título de la variante) en productos con variantes; vacío en los de variante única.
    color: String(raw.color || '').trim(),
    url: raw.url || '',
    price: Math.round(toNumber(raw.price) ?? 0),
    // Packs del producto (T-041, ADR 0017): escalones de custom.packs (texto de snippets/packs-data.liquid) y familia del escalón
    // (T-042: el handle del producto; cada góndola cuenta sola, con sus dos colores, como su descuento automático).
    packs: String(raw.packs || ''),
    packFamily: String(raw.pack_family || ''),
    available: Boolean(raw.available),
    tracked: raw.inventory_management === 'shopify' && raw.inventory_policy !== 'continue',
    inventory: Math.max(0, Math.trunc(toNumber(raw.inventory_quantity) ?? 0)),
    rol,
    tipo,
    alto: toNumber(raw.alto),
    // Ancho del módulo: ancho_modulo_m o, en los módulos completos, el ancho real (ancho_m).
    ancho: toNumber(raw.ancho_modulo) ?? (rol === 'modulo' ? toNumber(raw.ancho) : null),
    profundidad: toNumber(raw.profundidad),
    niveles: toNumber(raw.niveles),
    nivelesMax: toNumber(raw.niveles_max),
    unidadesArmado: toNumber(raw.unidades_armado) ?? 0,
    // 3D del módulo (ADR 0012): GLB del producto y nombre de la variante de material (color) o vacío.
    glb: String(raw.glb || ''),
    glbVariant: String(raw.glb_variant || ''),
  };
}

/** Cuántos módulos se dibujan en 3D (tope) y cuántos quedan afuera. */
export const MAX_3D = 15;
export function modules3d(n) {
  const shown = Math.min(Math.max(0, Math.trunc(n)), MAX_3D);
  return { shown, more: Math.max(0, Math.trunc(n) - shown) };
}

export const isComing = (variant) => variant?.state === 'proximamente';
export const isPreorder = (variant) => variant?.state === 'preventa';
export const canBuy = (variant) => !variant?.state || variant.state === 'comprable' || variant.state === 'preventa';

/** Unidades que se pueden vender de una variante (Infinity si no se controla stock). */
export function stockOf(variant) {
  if (!variant.available) return 0;
  return variant.tracked ? variant.inventory : Infinity;
}

/** De varias variantes que sirven para lo mismo, la primera con stock suficiente; si ninguna, la de más stock. */
export function pickVariant(candidates, qty) {
  if (!candidates.length) return null;
  const enough = candidates.find((v) => stockOf(v) >= qty);
  if (enough) return enough;
  return candidates.reduce((best, v) => (stockOf(v) > stockOf(best) ? v : best));
}

function candidatesFor(variants, { tipo, alto, rol }) {
  return variants.filter(
    (v) => v.rol === rol && v.tipo === tipo && (rol === 'estante_extra' || sameHeight(v.alto, alto)),
  );
}

/** Módulo base de un tipo y altura: el módulo completo si existe (modo modular), si no el inicial del kit. */
function baseFor(variants, tipo, alto) {
  const modulo = candidatesFor(variants, { tipo, alto, rol: 'modulo' });
  if (modulo.length) return { base: modulo, modular: true };
  return { base: candidatesFor(variants, { tipo, alto, rol: 'inicial' }), modular: false };
}

/** Colores disponibles (títulos de variante) del módulo base de un tipo y altura; vacío si es de variante única. */
export function getColors(variants, tipo, alto) {
  const { base } = baseFor(variants, tipo, alto);
  return [...new Set(base.map((v) => v.color).filter(Boolean))];
}

/** Tipos y alturas que se pueden calcular: hace falta al menos un módulo (inicial del kit o módulo completo) con alto y ancho. */
export function getOptions(variants) {
  const alturas = {};
  for (const v of variants) {
    if ((v.rol !== 'inicial' && v.rol !== 'modulo') || !v.tipo || !v.alto || !v.ancho) continue;
    const list = (alturas[v.tipo] ??= []);
    if (!list.some((a) => sameHeight(a, v.alto))) list.push(v.alto);
  }
  for (const list of Object.values(alturas)) list.sort((a, b) => a - b);
  return { tipos: TIPOS.filter((t) => alturas[t]), alturas };
}

/** Límites de los inputs para un tipo y altura. */
export function getLimits(variants, tipo, alto) {
  const { base, modular } = baseFor(variants, tipo, alto);
  const inicial = base[0];
  if (!inicial) return null;
  const niveles = Math.max(1, Math.round(inicial.niveles ?? 1));
  const extra = candidatesFor(variants, { tipo, alto, rol: 'estante_extra' }).filter((v) =>
    sameHeight(v.ancho, inicial.ancho),
  );
  const nivelesMax = extra.length ? Math.max(niveles, Math.round(inicial.nivelesMax ?? niveles)) : niveles;
  return {
    ancho: inicial.ancho,
    niveles,
    nivelesMax,
    modular,
    hasCabecera: tipo === 'central' && candidatesFor(variants, { tipo, alto, rol: 'cabecera' }).length > 0,
  };
}

/** Lleva un estado cualquiera (URL, inputs) a uno válido para los datos disponibles. */
export function normalizeState(variants, state = {}) {
  const options = getOptions(variants);
  if (!options.tipos.length) return null;
  const tipo = options.tipos.includes(state.tipo) ? state.tipo : options.tipos[0];
  const alturas = options.alturas[tipo];
  const altoPedido = toNumber(state.alto);
  // Sin altura pedida: la más alta que se pueda comprar ya (comprable o preventa); si ninguna, la más alta.
  const buyable = alturas.filter((a) => baseFor(variants, tipo, a).base.some(canBuy));
  const alto = alturas.find((a) => sameHeight(a, altoPedido)) ?? (buyable.length ? buyable : alturas).at(-1);
  const limits = getLimits(variants, tipo, alto);
  const largo = clamp(round2(toNumber(state.largo) ?? 6), 0, MAX_LARGO);
  const lineas = clamp(Math.round(toNumber(state.lineas) ?? 1), 1, MAX_LINEAS);
  const estantes = clamp(Math.round(toNumber(state.estantes) ?? limits.niveles), limits.niveles, limits.nivelesMax);
  const cabeceras = limits.hasCabecera && state.cabeceras !== false && state.cabeceras !== '0';
  const armado = state.armado !== false && state.armado !== '0';
  const colores = getColors(variants, tipo, alto);
  const color = colores.includes(String(state.color ?? '').trim()) ? String(state.color).trim() : colores[0] ?? '';
  return { tipo, alto, largo, lineas, estantes, cabeceras, armado, color };
}

/**
 * Arma la lista de módulos. Por góndola: 1 inicial + (n − 1) continuaciones, con n = ceil(largo / ancho), o n módulos
 * completos (modo modular: central por cada 1,20 m, pared por cada 0,90 m, según el ancho del producto);
 * central con cabeceras → 2 cabeceras; estantes extra = (estantes − incluidos) × n × caras. Con color elegido, las
 * variantes de ese color.
 */
export function computePlan(variants, state) {
  const empty = { status: 'vacio', items: [], n: 0, productos: 0, unidadesArmado: 0, armadoQty: 0 };
  if (!state) return { ...empty, status: 'sin-datos' };
  const { tipo, alto, largo, lineas, estantes, cabeceras } = state;
  const limits = getLimits(variants, tipo, alto);
  if (!limits) return { ...empty, status: 'sin-datos' };

  const n = largo > 0 ? Math.ceil(largo / limits.ancho - EPS) : 0;
  if (n === 0) return { ...empty, limits };

  const caras = tipo === 'central' ? 2 : 1;
  const extraPorCara = Math.max(0, estantes - limits.niveles);
  const pedidos = limits.modular
    ? [
        ['modulo', n * lineas],
        ['cabecera', cabeceras && limits.hasCabecera ? 2 * lineas : 0],
        ['estante_extra', extraPorCara * n * caras * lineas],
      ]
    : [
        ['inicial', lineas],
        ['continuacion', (n - 1) * lineas],
        ['cabecera', cabeceras && limits.hasCabecera ? 2 * lineas : 0],
        ['estante_extra', extraPorCara * n * caras * lineas],
      ];
  const color = String(state.color ?? '').trim();

  const items = [];
  for (const [rol, qty] of pedidos) {
    if (qty <= 0) continue;
    let candidates = candidatesFor(variants, { tipo, alto, rol });
    if (rol === 'estante_extra') candidates = candidates.filter((v) => sameHeight(v.ancho, limits.ancho));
    if (color) candidates = candidates.filter((v) => !v.color || v.color === color);
    const variant = pickVariant(candidates, qty);
    const stock = variant ? stockOf(variant) : 0;
    items.push({
      rol,
      qty,
      variant,
      subtotal: variant ? variant.price * qty : 0,
      short: Math.max(0, qty - stock),
    });
  }

  // Próximamente: todavía no hay stock que descontar; el precio es orientativo y nada va al carrito.
  const coming = items.some((item) => isComing(item.variant));
  if (coming) for (const item of items) item.short = 0;
  const comingHandles = [...new Set(items.filter((item) => isComing(item.variant)).map((item) => item.variant.handle).filter(Boolean))];
  // Preventa: se compra ya (con su stock reservado) y se entrega cuando llega el contenedor.
  const preorder = items.some((item) => isPreorder(item.variant));
  const transferProductos = applyPacks(items);
  const productos = items.reduce((sum, item) => sum + item.subtotal, 0);
  const unidadesArmado = round2(
    items.reduce((sum, item) => sum + item.qty * (item.variant?.unidadesArmado ?? 0), 0),
  );
  const cabecera = items.find((item) => item.rol === 'cabecera')?.variant;
  const metrosModulos = round2(n * limits.ancho);
  const inicial = items[0].variant;

  return {
    status: items.some((item) => !item.variant) ? 'sin-modelo' : items.some((item) => item.short > 0) ? 'falta-stock' : 'ok',
    coming,
    comingHandles,
    preorder,
    modular: limits.modular,
    limits,
    n,
    caras,
    items,
    productos,
    transferProductos,
    unidadesArmado,
    armadoQty: Math.ceil(unidadesArmado - EPS),
    metrosModulos,
    largoOcupado: round2(metrosModulos + (cabecera?.profundidad ? 2 * cabecera.profundidad : 0)),
    profundidad: inicial?.profundidad ?? null,
    cabeceraProfundidad: cabecera?.profundidad ?? null,
  };
}

/** T-041 (ADR 0017): precio de cada línea con el escalón de custom.packs según la cantidad total de su familia (Packs.priceLines,
 * assets/packs.js): subtotal web, precio por unidad y subtotal por transferencia. Sin packs.js o sin packs, precio × cantidad.
 * Devuelve el total de los productos por transferencia o null si ninguna línea tiene precio por transferencia. */
export function applyPacks(items) {
  const Packs = globalThis.Packs;
  const lines = items.filter((item) => item.variant);
  if (!Packs || !lines.length) return null;
  const priced = Packs.priceLines(lines.map((item) => ({ packs: item.variant.packs, family: item.variant.packFamily, price: item.variant.price, qty: item.qty })));
  lines.forEach((item, i) => {
    const line = priced.lines[i];
    Object.assign(item, { subtotal: line.subtotal, unit: line.unit, packMin: line.min, transferSubtotal: line.transferSubtotal });
  });
  return priced.hasTransfer ? priced.transfer : null;
}

/** Líneas para POST /cart/add.js. Devuelve [] si algo no se puede comprar completo o si algún producto es próximamente (tampoco el
 * armado). Las líneas de preventa llevan la propiedad «Entrega» (visible en carrito, checkout y pedido). */
export function cartItems(plan, armado, conArmado) {
  if (plan.status !== 'ok' || plan.coming || plan.items.some((item) => !canBuy(item.variant))) return [];
  const items = plan.items.map((item) => {
    const line = { id: item.variant.id, quantity: item.qty };
    if (item.variant.delivery) line.properties = { [item.variant.delivery.key]: item.variant.delivery.value };
    return line;
  });
  if (conArmado && armado?.id && plan.armadoQty > 0) items.push({ id: armado.id, quantity: plan.armadoQty });
  return items;
}

export function totals(plan, armado, conArmado) {
  const armadoTotal = conArmado && armado ? plan.armadoQty * armado.price : 0;
  return { productos: plan.productos, armado: armadoTotal, total: plan.productos + armadoTotal };
}

/** Estado ↔ query string, para compartir el resultado por link. */
export function stateToParams(state) {
  const params = new URLSearchParams();
  params.set('tipo', state.tipo);
  params.set('largo', String(state.largo));
  params.set('lineas', String(state.lineas));
  params.set('alto', String(state.alto));
  params.set('estantes', String(state.estantes));
  params.set('cabeceras', state.cabeceras ? '1' : '0');
  params.set('armado', state.armado ? '1' : '0');
  if (state.color) params.set('color', state.color);
  return params;
}

export function paramsToState(search) {
  const params = new URLSearchParams(search);
  const state = {};
  for (const key of ['tipo', 'largo', 'lineas', 'alto', 'estantes', 'cabeceras', 'armado', 'color']) {
    if (params.has(key)) state[key] = params.get(key);
  }
  return state;
}

/** Reemplaza [clave] en los textos traducidos. */
export function fill(template, values) {
  return String(template ?? '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
}

export function formatters(currency = 'UYU') {
  const money = new Intl.NumberFormat('es-UY', { style: 'currency', currency, maximumFractionDigits: 0, minimumFractionDigits: 0 });
  const meters = new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return { money: (cents) => money.format(cents / 100), meters: (m) => meters.format(m) };
}

/** Texto para WhatsApp con el resumen del cálculo. */
export function whatsappText({ plan, state, armado, strings, fmt, link }) {
  const t = totals(plan, armado, state.armado);
  const lines = [
    fill(strings.wa_intro, {}),
    fill(strings.wa_config, {
      tipo: strings[`tipo_${state.tipo}`],
      alto: fmt.meters(state.alto),
      largo: fmt.meters(state.largo),
      lineas: state.lineas,
      estantes: state.estantes,
    }),
    ...(state.color && strings.wa_color ? [fill(strings.wa_color, { color: state.color })] : []),
    ...plan.items.map((item) => `- ${item.qty} × ${item.variant ? item.variant.title : strings[`rol_${item.rol}`]}`),
    fill(strings.wa_productos, { total: fmt.money(t.productos) }),
  ];
  if (state.armado && armado) {
    lines.push(fill(strings.wa_armado, { unidades: plan.armadoQty, total: fmt.money(t.armado) }));
  }
  lines.push(fill(strings.wa_total, { total: fmt.money(t.total) }));
  // T-045: total por transferencia (productos al escalón de su familia + armado); se coordina por WhatsApp.
  if (plan.transferProductos != null && !plan.coming && strings.wa_transfer) lines.push(fill(strings.wa_transfer, { total: fmt.money(plan.transferProductos + t.armado) }));
  if (link) lines.push(link);
  return lines.join('\n');
}

const escapeXml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/**
 * Dibujo técnico de la góndola: frente (elevación) y planta, a escala, con cotas.
 * Devuelve el markup interno de dos <svg>. Muestra una góndola; si son varias, lo indica con "× N".
 */
export function drawing(plan, state, strings, fmt, width = 960) {
  if (!plan.n) return { frente: '', planta: '' };
  // Se dibuja al ancho real en px para que los textos de las cotas no se achiquen en mobile.
  const W = Math.max(280, Math.round(width));
  const padL = W < 480 ? 40 : 64;
  const padR = 24;
  const cab = state.cabeceras && plan.cabeceraProfundidad ? plan.cabeceraProfundidad : 0;
  const totalLen = plan.metrosModulos + 2 * cab;
  const scale = Math.min((W - padL - padR) / Math.max(totalLen, state.largo), 240 / state.alto);
  const px = (m) => round2(m * scale);
  const x0 = padL + px(cab);
  const xEnd = x0 + px(plan.metrosModulos);
  const ancho = plan.limits.ancho;

  // Frente
  const top = 28;
  const h = px(state.alto);
  const floor = top + h;
  const parts = [];
  if (state.lineas > 1) {
    parts.push(`<text class="calc-svg__note" x="${W - padR}" y="16" text-anchor="end">${escapeXml(fill(strings.svg_lineas, { lineas: state.lineas }))}</text>`);
  }
  if (cab) {
    parts.push(`<rect class="calc-svg__cab" x="${padL}" y="${top + px(0.1)}" width="${px(cab)}" height="${h - px(0.1)}"/>`);
    parts.push(`<rect class="calc-svg__cab" x="${xEnd}" y="${top + px(0.1)}" width="${px(cab)}" height="${h - px(0.1)}"/>`);
  }
  const base = 0.12;
  const usable = state.alto - base - 0.3;
  for (let i = 0; i < plan.n; i++) {
    const x = x0 + px(i * ancho);
    parts.push(`<rect class="calc-svg__mod${i === 0 && !plan.modular ? ' calc-svg__mod--inicial' : ''}" x="${x}" y="${top}" width="${px(ancho)}" height="${h}"/>`);
    for (let k = 0; k < state.estantes; k++) {
      const y = round2(floor - px(base + (usable * k) / Math.max(1, state.estantes - 1)));
      const extra = k >= plan.limits.niveles;
      parts.push(`<line class="calc-svg__shelf${extra ? ' calc-svg__shelf--extra' : ''}" x1="${x + 3}" x2="${x + px(ancho) - 3}" y1="${y}" y2="${y}"/>`);
    }
  }
  for (let i = 0; i <= plan.n; i++) {
    const x = x0 + px(i * ancho);
    const inicial = i <= 1 && !plan.modular;
    parts.push(`<line class="calc-svg__post${inicial ? ' calc-svg__post--inicial' : ''}" x1="${x}" x2="${x}" y1="${top}" y2="${floor}"/>`);
  }
  parts.push(`<line class="calc-svg__floor" x1="${padL - 8}" x2="${W - padR + 8}" y1="${floor}" y2="${floor}"/>`);
  // Largo pedido, si no coincide con los módulos
  if (state.largo > 0 && Math.abs(state.largo - plan.metrosModulos) > 0.005) {
    const xr = x0 + px(state.largo);
    parts.push(`<line class="calc-svg__pedido" x1="${xr}" x2="${xr}" y1="${top - 10}" y2="${floor + 6}"/>`);
    parts.push(`<text class="calc-svg__note" x="${xr - 4}" y="${top - 14}" text-anchor="end">${escapeXml(fill(strings.svg_pedido, { largo: fmt.meters(state.largo) }))}</text>`);
  }
  // Cotas
  const cy = floor + 24;
  parts.push(dimH(x0, xEnd, cy, fill(strings.svg_cota_largo, { n: plan.n, ancho: fmt.meters(ancho), largo: fmt.meters(plan.metrosModulos) })));
  parts.push(dimV(padL - (W < 480 ? 20 : 32), top, floor, `${fmt.meters(state.alto)} m`));
  const frenteH = cy + 22;
  const frente = `<svg class="calc-svg" viewBox="0 0 ${W} ${frenteH}" role="img">${parts.join('')}</svg>`;

  // Planta
  let planta = '';
  if (plan.profundidad) {
    const d = px(plan.profundidad);
    const pt = state.tipo === 'pared' ? 22 : 12;
    const p = [];
    if (state.tipo === 'pared') {
      p.push(`<line class="calc-svg__wall" x1="${padL - 8}" x2="${W - padR + 8}" y1="${pt - 4}" y2="${pt - 4}"/>`);
    }
    if (cab) {
      p.push(`<rect class="calc-svg__cab" x="${padL}" y="${pt}" width="${px(cab)}" height="${d}"/>`);
      p.push(`<rect class="calc-svg__cab" x="${xEnd}" y="${pt}" width="${px(cab)}" height="${d}"/>`);
    }
    for (let i = 0; i < plan.n; i++) {
      p.push(`<rect class="calc-svg__mod${i === 0 && !plan.modular ? ' calc-svg__mod--inicial' : ''}" x="${x0 + px(i * ancho)}" y="${pt}" width="${px(ancho)}" height="${d}"/>`);
    }
    if (state.tipo === 'central') {
      p.push(`<line class="calc-svg__spine" x1="${x0}" x2="${xEnd}" y1="${pt + d / 2}" y2="${pt + d / 2}"/>`);
    }
    const py = pt + d + 22;
    p.push(
      dimH(padL, xEnd + px(cab), py, fill(strings.svg_cota_total, { largo: fmt.meters(plan.largoOcupado), fondo: fmt.meters(plan.profundidad) })),
    );
    planta = `<svg class="calc-svg" viewBox="0 0 ${W} ${py + 22}" role="img">${p.join('')}</svg>`;
  }
  return { frente, planta };
}

function dimH(x1, x2, y, label) {
  return (
    `<g class="calc-svg__dim"><line x1="${x1}" x2="${x2}" y1="${y}" y2="${y}"/>` +
    `<line x1="${x1}" x2="${x1}" y1="${y - 6}" y2="${y + 6}"/><line x1="${x2}" x2="${x2}" y1="${y - 6}" y2="${y + 6}"/>` +
    `<text x="${round2((x1 + x2) / 2)}" y="${y + 18}" text-anchor="middle">${escapeXml(label)}</text></g>`
  );
}

function dimV(x, y1, y2, label) {
  const my = round2((y1 + y2) / 2);
  return (
    `<g class="calc-svg__dim"><line x1="${x}" x2="${x}" y1="${y1}" y2="${y2}"/>` +
    `<line x1="${x - 6}" x2="${x + 6}" y1="${y1}" y2="${y1}"/><line x1="${x - 6}" x2="${x + 6}" y1="${y2}" y2="${y2}"/>` +
    `<text x="${x - 8}" y="${my}" text-anchor="middle" transform="rotate(-90 ${x - 8} ${my})">${escapeXml(label)}</text></g>`
  );
}

/* ------------------------------------------------------------------ */
/* Web component                                                       */
/* ------------------------------------------------------------------ */

const Base = globalThis.HTMLElement ?? class {};

export class CalcGondolas extends Base {
  connectedCallback() {
    const script = this.querySelector('[data-calc-data]');
    try {
      this.data = JSON.parse(script?.textContent || '{}');
    } catch {
      this.data = {};
    }
    this.strings = this.data.strings || {};
    this.fmt = formatters(this.data.currency || 'UYU');
    this.variants = (this.data.variants || []).map(normalizeVariant);
    this.armado = this.data.armado || null;
    this.form = this.querySelector('[data-calc-form]');
    this.result = this.querySelector('[data-calc-result]');

    this.state = normalizeState(this.variants, paramsToState(window.location.search));
    if (!this.state) {
      this.toggleAttribute('data-empty', true);
      return;
    }
    if (!window.location.search.includes('largo=') && this.dataset.largo) {
      this.state = normalizeState(this.variants, { ...this.state, largo: this.dataset.largo });
    }
    this.renderHeights();
    this.renderColors();
    this.syncInputs();
    this.bind();
    this.update({ initial: true });
    this.init3d();
    this.trackView();
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        const width = this.querySelector('[data-calc-frente]').clientWidth;
        if (width && Math.abs(width - this.drawnWidth) > 8) this.renderDrawing(this.plan);
      }).observe(this.querySelector('.calc__drawing'));
    }
  }

  bind() {
    this.form.addEventListener('submit', (event) => event.preventDefault());
    this.form.addEventListener('input', (event) => this.onInput(event));
    this.form.addEventListener('change', () => {
      // Al salir del campo se muestra el valor corregido (topes, vacío → 0).
      this.syncInputs();
      this.track('calc_change', this.analyticsPayload());
    });
    this.form.addEventListener('click', (event) => {
      const step = event.target.closest('[data-step]');
      if (!step) return;
      const input = this.form.elements[step.dataset.target];
      const delta = toNumber(step.dataset.step) * (toNumber(input.step) || 1);
      input.value = String(round2((toNumber(input.value) ?? 0) + delta));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.result.addEventListener('change', (event) => {
      if (event.target.name !== 'armado') return;
      this.state.armado = event.target.checked;
      this.update();
    });
    this.result.addEventListener('click', (event) => {
      if (event.target.closest('[data-calc-add]')) this.addToCart();
      if (event.target.closest('[data-calc-whatsapp]')) this.track('calc_whatsapp', this.analyticsPayload());
    });
  }

  onInput(event) {
    const { name } = event.target;
    const els = this.form.elements;
    if (name === 'largo-range') els.largo.value = event.target.value;
    const next = {
      tipo: els.tipo.value,
      largo: els.largo.value === '' ? 0 : els.largo.value,
      lineas: els.lineas.value,
      alto: els.alto?.value ?? this.state.alto,
      estantes: els.estantes.value,
      cabeceras: els.cabeceras.checked,
      armado: this.state.armado,
      color: els.color?.value ?? this.state.color,
    };
    if (name === 'tipo' || name === 'alto') next.estantes = null;
    if (name === 'tipo') next.cabeceras = true;
    const prevTipo = this.state.tipo;
    const prevAlto = this.state.alto;
    this.state = normalizeState(this.variants, next);
    if (this.state.tipo !== prevTipo) this.renderHeights();
    if (this.state.tipo !== prevTipo || this.state.alto !== prevAlto) this.renderColors();
    // No pisar lo que la persona está escribiendo en el campo numérico.
    this.syncInputs(event.target);
    this.update();
  }

  renderHeights() {
    const wrap = this.querySelector('[data-calc-alturas]');
    const alturas = getOptions(this.variants).alturas[this.state.tipo] || [];
    wrap.replaceChildren(
      ...alturas.map((alto) => {
        const label = document.createElement('label');
        label.className = 'calc__chip';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'alto';
        input.value = String(alto);
        input.checked = sameHeight(alto, this.state.alto);
        label.append(input, document.createTextNode(`${this.fmt.meters(alto)} m`));
        return label;
      }),
    );
  }

  /** Chips de color (variantes del módulo base del tipo y altura elegidos); el campo se oculta con un solo color. */
  renderColors() {
    const wrap = this.querySelector('[data-calc-colores]');
    const field = this.querySelector('[data-calc-color-field]');
    if (!wrap) return;
    const colores = getColors(this.variants, this.state.tipo, this.state.alto);
    wrap.replaceChildren(
      ...colores.map((color) => {
        const label = document.createElement('label');
        label.className = 'calc__chip';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'color';
        input.value = color;
        input.checked = color === this.state.color;
        label.append(input, document.createTextNode(color));
        return label;
      }),
    );
    if (field) field.hidden = colores.length < 2;
  }

  syncInputs(active) {
    const els = this.form.elements;
    const { tipo, alto, largo, lineas, estantes, cabeceras, color } = this.state;
    const limits = getLimits(this.variants, tipo, alto);
    const tipos = getOptions(this.variants).tipos;
    for (const radio of this.form.querySelectorAll('input[name="tipo"]')) {
      radio.disabled = !tipos.includes(radio.value);
      radio.checked = radio.value === tipo;
    }
    for (const radio of this.form.querySelectorAll('input[name="alto"]')) radio.checked = sameHeight(toNumber(radio.value), alto);
    for (const radio of this.form.querySelectorAll('input[name="color"]')) radio.checked = radio.value === color;
    if (active !== els.largo) els.largo.value = String(largo);
    els['largo-range'].value = String(Math.min(largo, toNumber(els['largo-range'].max)));
    if (active !== els.lineas) els.lineas.value = String(lineas);
    els.estantes.min = String(limits.niveles);
    els.estantes.max = String(limits.nivelesMax);
    if (active !== els.estantes) els.estantes.value = String(estantes);
    els.estantes.closest('[data-field]').toggleAttribute('data-fixed', limits.niveles === limits.nivelesMax);
    els.cabeceras.checked = cabeceras;
    els.cabeceras.closest('[data-field]').hidden = !limits.hasCabecera;
  }

  update({ initial = false } = {}) {
    // El link compartible va primero: el de WhatsApp se arma con él.
    const url = new URL(window.location.href);
    // Solo nuestras claves: no pisar otros parámetros (view, preview_theme_id, utm_*…).
    for (const [key, value] of stateToParams(this.state)) url.searchParams.set(key, value);
    this.shareUrl = url.href;
    // Al cargar no se toca la barra de direcciones (en la home quedaría ?tipo=…); recién al interactuar.
    if (!initial) window.history.replaceState(window.history.state, '', url);
    const plan = computePlan(this.variants, this.state);
    this.plan = plan;
    this.renderResult(plan);
    this.renderDrawing(plan);
    this.schedule3d();
  }

  /* ---------- Línea completa en 3D (ADR 0012, parte D) ---------- */

  /** Solo con WebGL y sin movimiento reducido: carga assets/scene-3d.js, crea el visor una vez y arma la primera línea. */
  async init3d() {
    this.box3d = this.querySelector('[data-calc-3d]');
    if (!this.box3d || !this.box3d.dataset.scene) return;
    try { this.scene = await import(this.box3d.dataset.scene); } catch { this.box3d.hidden = true; return; }
    if (!this.scene.canShow3d()) { this.box3d.hidden = true; return; }
    // Celular o red limitada: el panel queda con «Tocá para verla en 3D»; si no, carga al entrar en pantalla (ADR 0013).
    await this.scene.whenAllowed(this.box3d.querySelector('[data-calc-3d-stage]'), this.box3d.querySelector('[data-calc-3d-start]'));
    if (!this.scene.supportsWebGL() || !(await this.scene.loadViewer())) { this.box3d.hidden = true; return; }
    // Línea larga: vista más de frente y cerca en desktop; en mobile (visor casi cuadrado) más lejos para que entre entera con sus cotas.
    const narrow = matchMedia('(max-width: 767px)').matches;
    this.viewer = this.scene.createViewer({ alt: this.strings.view3d_alt || '', orbit: narrow ? '24deg 74deg 100%' : '22deg 76deg 78%', maxRadius: '140%' });
    const ar = document.createElement('button');
    ar.type = 'button'; ar.slot = 'ar-button'; ar.className = 'build__ar'; ar.textContent = this.strings.ar_local || '';
    this.viewer.append(ar);
    this.box3d.querySelector('[data-calc-3d-canvas]').append(this.viewer);
    this.dims3d = new this.scene.SceneDims(this.viewer, this.box3d.querySelector('[data-svg]'), this.box3d.querySelector('[data-labels]'));
    this.viewer.addEventListener('load', () => this.onScene3dLoad());
    this.box3d.hidden = false;
    this.schedule3d();
  }

  /** Debounce: metros, tipo y color cambian seguido; la escena se rearma 150 ms después del último cambio. */
  schedule3d() {
    if (!this.viewer) return;
    clearTimeout(this.timer3d);
    this.timer3d = setTimeout(() => this.render3d(), 150);
  }

  /** N módulos (tope MAX_3D) del color elegido en fila: una copia del árbol de nodos por módulo, mallas compartidas. */
  async render3d() {
    const plan = this.plan;
    const token = (this.token3d = (this.token3d || 0) + 1);
    const module = plan?.items?.find((item) => item.rol === 'modulo' || item.rol === 'inicial')?.variant;
    const { shown, more } = modules3d(plan?.n || 0);
    const note = this.box3d.querySelector('[data-calc-3d-more]');
    if (!module?.glb || !shown) {
      this.box3d.hidden = true;
      return;
    }
    this.box3d.hidden = false;
    const key = `${module.glb}|${module.glbVariant}|${shown}`;
    if (key === this.sceneKey) return;
    this.viewer.classList.add('is-loading');
    let glb;
    let tools;
    try {
      [glb, tools] = await Promise.all([this.scene.loadGlb(module.glb, this.box3d.dataset.glb), this.scene.glbTools(this.box3d.dataset.glb)]);
    } catch { this.box3d.hidden = true; return; }
    if (token !== this.token3d) return;
    const width = tools.boundsOf(glb.json)?.size[0] || module.ancho || 1;
    const composed = tools.compose([{ glb, variant: module.glbVariant, instances: tools.inRow(shown, width).map((x) => [x, 0, 0]) }]);
    this.sceneKey = key;
    this.pending3d = { composed, width, shown, more, module };
    this.scene.setSceneBuffer(this.viewer, composed.buffer);
    const largoTotal = this.fmt.meters(round2((plan.n || 0) * width));
    note.hidden = more <= 0;
    note.textContent = more > 0 ? fill(this.strings.more_modules, { n: more, largo: largoTotal }) : '';
  }

  /** Línea cargada: cotas totales (largo de lo dibujado, alto y fondo) sobre la caja de la escena. */
  onScene3dLoad() {
    this.viewer.classList.remove('is-loading');
    const pending = this.pending3d;
    if (!pending) return;
    this.pending3d = null;
    const { composed, module } = pending;
    const boxes = composed.parts[0]?.bounds || [];
    if (!boxes.length) return;
    const min = boxes[0].min.slice();
    const max = boxes[0].max.slice();
    for (const b of boxes) for (let i = 0; i < 3; i++) { min[i] = Math.min(min[i], b.min[i]); max[i] = Math.max(max[i], b.max[i]); }
    const m = (v) => fill(this.strings.dim_m, { m: this.fmt.meters(v) });
    this.dims3d.set([{ key: 'linea', min, max, texts: { w: m(max[0] - min[0]), h: m(module.alto ?? max[1] - min[1]), d: m(module.profundidad ?? max[2] - min[2]) } }]);
    this.scene.frameLow(this.viewer, { min, max });
    const narrow = matchMedia('(max-width: 767px)').matches;
    this.scene.fitWidth(this.viewer, narrow ? '24deg' : '22deg', narrow ? '74deg' : '76deg', 0.8);
    this.viewer.jumpCameraToGoal?.();
    this.viewer.classList.add('is-ready');
    this.dispatchEvent(new CustomEvent('calc:scene3d', { detail: { modules: pending.shown, more: pending.more, size: [max[0] - min[0], max[1] - min[1], max[2] - min[2]] } }));
  }

  renderDrawing(plan) {
    const width = this.querySelector('[data-calc-frente]').clientWidth || 960;
    this.drawnWidth = width;
    const planKey = `${this.state.tipo}:${plan.n}:${this.state.estantes}`;
    this.toggleAttribute('data-calc-transition', Boolean(this.drawnPlanKey && this.drawnPlanKey !== planKey));
    this.drawnPlanKey = planKey;
    const metrics = this.querySelector('[data-calc-measurements]');
    if (metrics) {
      metrics.hidden = !plan.n;
      this.querySelector('[data-calc-module-count]').textContent = String(plan.n);
      this.querySelector('[data-calc-occupied]').textContent = this.fmt.meters(plan.largoOcupado);
    }
    const emptyDrawing = this.querySelector('[data-calc-drawing-empty]');
    if (emptyDrawing) emptyDrawing.hidden = Boolean(plan.n);
    // Leyenda: inicial/continuación en kits, «Módulo completo» en modo modular.
    this.querySelectorAll('[data-calc-legend-kit]').forEach((li) => { li.hidden = Boolean(plan.modular); });
    this.querySelectorAll('[data-calc-legend-modulo]').forEach((li) => { li.hidden = !plan.modular; });
    const optionsSummary = this.querySelector('[data-calc-options-summary]');
    if (optionsSummary) optionsSummary.textContent = fill(this.strings.options_summary, {
      alto: this.fmt.meters(this.state.alto), lineas: this.state.lineas, estantes: this.state.estantes,
    });
    const { frente, planta } = drawing(plan, this.state, this.strings, this.fmt, width);
    this.querySelector('[data-calc-frente]').innerHTML = frente;
    this.querySelector('[data-calc-planta]').innerHTML = planta;
    const caption = this.querySelector('[data-calc-caption]');
    caption.textContent = plan.n
      ? fill(this.strings.svg_caption, {
          tipo: String(this.strings[`tipo_${this.state.tipo}`] ?? '').toLocaleLowerCase('es'),
          n: plan.n,
          largo: this.fmt.meters(plan.metrosModulos),
          alto: this.fmt.meters(this.state.alto),
          estantes: this.state.estantes,
        })
      : '';
    // El pie describe el conjunto: lo anuncia el frente y la planta queda como decorativa.
    this.querySelector('[data-calc-frente] svg')?.setAttribute('aria-labelledby', caption.id);
    this.querySelector('[data-calc-planta] svg')?.setAttribute('aria-hidden', 'true');
  }

  renderResult(plan) {
    const s = this.strings;
    const { money, meters } = this.fmt;
    const el = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    };

    const live = this.querySelector('[data-calc-live]');
    if (!plan.n) {
      this.result.replaceChildren(el('p', 'calc__hint', s.vacio));
      live.textContent = s.vacio;
      return;
    }

    const summary = el(
      'p',
      'calc__summary',
      fill(plan.items.some((i) => i.rol === 'cabecera') ? s.resumen_cabeceras : s.resumen, {
        n: plan.n,
        metros: meters(plan.metrosModulos),
        ocupado: meters(plan.largoOcupado),
      }),
    );

    const list = el('ul', 'calc__list');
    for (const item of plan.items) {
      const li = el('li', 'calc__item');
      const name = el('span', 'calc__item-name');
      name.append(el('span', 'calc__qty', `${item.qty} ×`), ' ');
      if (item.variant) {
        const link = el('a', null, item.variant.title);
        link.href = item.variant.url;
        name.append(link);
        if (isComing(item.variant)) name.append(el('span', 'calc__badge calc__badge--coming', s.proximamente));
        else if (isPreorder(item.variant) && s.preventa) name.append(el('span', 'calc__badge calc__badge--preventa', s.preventa));
      } else {
        name.append(el('span', null, s[`rol_${item.rol}`]));
      }
      li.append(name, el('span', 'calc__money', item.variant ? money(item.subtotal) : '—'));
      // T-041: con el escalón de un pack (5 o más de la familia), el precio por unidad que lo explica.
      if (item.variant && item.packMin > 1 && this.data.packs?.unit) li.append(el('span', 'calc__unit', fill(this.data.packs.unit, { price: money(item.unit) })));
      if (!item.variant) li.append(el('span', 'calc__warn', s.sin_modelo));
      else if (item.short > 0 && !plan.coming) {
        const stock = item.qty - item.short;
        li.append(el('span', 'calc__warn', stock > 0 ? fill(s.falta_stock, { stock }) : s.sin_stock));
      }
      list.append(li);
    }

    const t = totals(plan, this.armado, this.state.armado);
    const rows = el('dl', 'calc__totals');
    const row = (label, value, className) => {
      const wrap = el('div', className);
      wrap.append(el('dt', null, label), el('dd', 'calc__money', value));
      rows.append(wrap);
    };
    row(plan.coming ? s.orientativo : s.productos, money(t.productos));

    const armadoBox = el('div', 'calc__armado');
    if (this.armado) {
      const toggle = el('label', 'calc__switch');
      const input = el('input');
      input.type = 'checkbox';
      input.name = 'armado';
      input.checked = this.state.armado;
      toggle.append(input, el('span', null, s.armado_toggle));
      armadoBox.append(toggle);
      if (this.state.armado && plan.armadoQty > 0) {
        armadoBox.append(
          el('p', 'calc__hint', fill(s.armado_detalle, { unidades: plan.armadoQty, tarifa: money(this.armado.price) })),
        );
        row(s.armado, money(t.armado));
      }
    }
    row(plan.coming ? s.total_orientativo : this.state.armado && t.armado ? s.total_con_armado : s.total, money(t.total), 'calc__total');

    // T-041: total por transferencia = productos al precio por transferencia del escalón de su familia (custom.packs) + armado.
    const packs = this.data.packs;
    let transferNote = null;
    if (!plan.coming && plan.transferProductos != null && packs) {
      const wrap = el('div', 'calc__transfer');
      wrap.append(el('dt', null, packs.label), el('dd', 'calc__money', money(plan.transferProductos + t.armado)));
      rows.append(wrap);
      transferNote = el('p', 'calc__transfer-note', packs.note);
    }

    const actions = el('div', 'calc__actions');
    const items = cartItems(plan, this.armado, this.state.armado);
    const notify = this.querySelector('[data-calc-notify]');
    if (plan.coming && notify) {
      // Próximamente: en vez de «Agregar todo al carrito», el form «Avisame cuando llegue» con un tag aviso-{handle} por producto.
      // El form puede seguir en su <template> (notify.js lo clona al abrir): los tags van al elemento y al input si ya existe.
      const tags = ['aviso', ...plan.comingHandles.map((h) => `aviso-${h}`)].join(',');
      const box = notify.querySelector('notify-form');
      if (box?.setTags) box.setTags(tags); else if (box) box.dataset.tags = tags;
      notify.hidden = false;
      actions.append(notify);
    } else {
      if (notify) { notify.hidden = true; this.append(notify); }
      const add = el('button', 'calc__btn calc__btn--primary', s.agregar);
      add.type = 'button';
      add.dataset.calcAdd = '';
      add.disabled = items.length === 0;
      actions.append(add);
    }
    if (this.data.whatsapp) {
      const wa = el('a', 'calc__btn calc__btn--secondary', s.whatsapp);
      wa.dataset.calcWhatsapp = '';
      wa.target = '_blank';
      wa.rel = 'noopener';
      wa.href = `https://wa.me/${this.data.whatsapp}?text=${encodeURIComponent(
        whatsappText({ plan, state: this.state, armado: this.armado, strings: s, fmt: this.fmt, link: this.shareUrl }),
      )}`;
      actions.append(wa);
    }

    const status = el('p', 'calc__status');
    status.dataset.calcStatus = '';
    if (plan.status === 'falta-stock') status.textContent = s.estado_falta_stock;
    if (plan.status === 'sin-modelo') status.textContent = s.estado_sin_modelo;
    if (plan.coming) status.textContent = s.estado_proximamente;
    else if (plan.preorder && s.estado_preventa) status.textContent = s.estado_preventa;

    this.result.replaceChildren(summary, list, armadoBox, rows, ...(transferNote ? [transferNote] : []), el('p', 'calc__iva', s.iva), status, actions);
    const totalLabel = this.state.armado && t.armado ? s.total_con_armado : s.total;
    live.textContent = `${summary.textContent} ${totalLabel}: ${money(t.total)}. ${status.textContent}`.trim();
  }

  async addToCart() {
    const items = cartItems(this.plan, this.armado, this.state.armado);
    if (!items.length) return;
    const button = this.result.querySelector('[data-calc-add]');
    const status = this.result.querySelector('[data-calc-status]');
    if (!button) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = this.strings.agregando;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || this.strings.error);
      this.track('calc_add_to_cart', { ...this.analyticsPayload(), items });
      document.dispatchEvent(new CustomEvent('cart:items-added', { detail: { source: 'calc-gondolas', items: body.items ?? items } }));
      const drawer = document.querySelector('cart-drawer');
      if (typeof drawer?.open === 'function') {
        drawer.open();
        status.textContent = this.strings.agregado;
      } else {
        status.replaceChildren(this.strings.agregado, ' ');
        const link = document.createElement('a');
        link.href = this.data.cartUrl || '/cart';
        link.textContent = this.strings.ver_carrito;
        status.append(link);
      }
    } catch (error) {
      status.textContent = error.message || this.strings.error;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  analyticsPayload() {
    const t = this.plan ? totals(this.plan, this.armado, this.state.armado) : {};
    return { calculadora: 'gondolas', ...this.state, modulos: this.plan?.n ?? 0, total: (t.total ?? 0) / 100 };
  }

  track(name, payload) {
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch {
      /* analytics nunca rompe la calculadora */
    }
  }

  trackView() {
    if (!('IntersectionObserver' in window)) return this.track('calc_view', this.analyticsPayload());
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      this.track('calc_view', this.analyticsPayload());
    });
    observer.observe(this);
  }
}

if (globalThis.customElements && !customElements.get('calc-gondolas')) {
  customElements.define('calc-gondolas', CalcGondolas);
}
